import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Node = ({ icon, label, x, y, delay = 0, color = "blue" }) => {
  const colors = {
    blue: "from-blue-500 to-cyan-400 shadow-blue-500/20",
    purple: "from-purple-500 to-indigo-400 shadow-purple-500/20",
    orange: "from-orange-500 to-red-400 shadow-orange-500/20",
    emerald: "from-emerald-500 to-teal-400 shadow-emerald-500/20",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, type: "spring", stiffness: 100 }}
      style={{ left: `${x}%`, top: `${y}%` }}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 md:gap-3 z-10"
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: delay * 0.5 }}
        className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${colors[color]} p-[1px] shadow-2xl group cursor-pointer`}
      >
        <div className="w-full h-full bg-slate-900 rounded-[15px] flex items-center justify-center group-hover:bg-slate-800 transition-colors">
          <SafeIcon icon={icon} className="text-xl md:text-2xl text-white" />
        </div>
      </motion.div>
      <span className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap bg-slate-900/80 px-2 py-1 rounded backdrop-blur-sm border border-white/5">
        {label}
      </span>
    </motion.div>
  );
};

const EcosystemVisual = () => {
  // Adjusted positions: Pulled extreme nodes inward (closer to 20-80 range) to prevent clipping
  const satelliteNodes = [
    { id: 'reddit', icon: FiIcons.FiMessageSquare, label: "REDDIT", color: "orange", x: 22, y: 30, delay: 0.4 },
    { id: 'quora', icon: FiIcons.FiHelpCircle, label: "QUORA", color: "purple", x: 78, y: 18, delay: 0.6 },
    { id: 'backlinks', icon: FiIcons.FiLink, label: "BACKLINKS", color: "emerald", x: 18, y: 68, delay: 0.8 }, 
    { id: 'pr', icon: FiIcons.FiTrendingUp, label: "PR NEWS", color: "blue", x: 78, y: 74, delay: 1.0 },
    { id: 'google', icon: FiIcons.FiSearch, label: "GOOGLE", color: "blue", x: 50, y: 15, delay: 1.2 },
    { id: 'ai', icon: FiIcons.FiCpu, label: "AI SEARCH", color: "emerald", x: 80, y: 42, delay: 1.4 } // Pulled inward from 90 to 80
  ];

  // Dynamically create the SVG path connecting center (50, 50) to all satellite nodes
  const pathData = satelliteNodes.map(n => `M 50,50 L ${n.x},${n.y}`).join(' ');

  return (
    <div className="relative w-full aspect-[4/3] bg-slate-900/20 rounded-3xl border border-white/5 overflow-hidden">
      {/* Animated Connection Lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {/* Connection Paths */}
        <motion.path
          d={pathData}
          stroke="url(#lineGrad)"
          strokeWidth="0.5"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />

        {/* Pulsing Signal on lines */}
        {satelliteNodes.map((node, i) => (
          <motion.circle
            key={`pulse-${node.id}`}
            r="0.8"
            fill="#3B82F6"
            initial={{ offsetDistance: "0%" }}
            animate={{ offsetDistance: "100%" }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.5, ease: "linear" }}
            style={{ offsetPath: `path('M 50,50 L ${node.x},${node.y}')` }}
          />
        ))}
      </svg>

      {/* Center Core Node */}
      <Node x={50} y={50} icon={FiIcons.FiTarget} label="SEO CORE" color="blue" delay={0.2} />

      {/* Satellite Nodes */}
      {satelliteNodes.map(node => (
        <Node key={node.id} {...node} />
      ))}

      {/* Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/10 blur-[100px] rounded-full pointer-events-none"></div>
    </div>
  );
};

export default EcosystemVisual;